import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play, DollarSign, ShoppingCart, Wrench, ArrowUpRight, ArrowDownRight,
  AlertTriangle, TrendingDown, TrendingUp, ListChecks, HelpCircle, Search,
  Settings, RefreshCcw, CheckCircle2, MapPin, ArrowRight, Building2,
} from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';

import PeriodPicker from '../../components/mec/PeriodPicker';
import MetricTile from '../../components/mec/MetricTile';
import InventoryExclusionCard from '../../components/mec/InventoryExclusionCard';
import RecentClosesPanel from '../../components/mec/RecentClosesPanel';
import { usePeriodState } from '../../components/mec/usePeriodState';
import { bucketLabel } from '../../components/mec/mecLabels';

import { listRouting, runClose, getClose } from '../../api/mec';
import { formatMoney } from '../../utils/formatters';
import { useShop } from '../../hooks/useShop';

// MEC Dashboard (View 1 per MEC_REQUIREMENTS.md §7) — SINGLE-shop deep-dive.
// The TopBar's shop selector (driven by useShop()) is the single source of
// truth for which shop is shown. Multi-shop comparison lives at
// /mec/locations.
//
// Data model:
//   close: normalized result for the current shop in the current period (or
//          {error} on failure, null when no run yet).
function normalizeCloseResult(raw) {
  if (!raw) return null;
  if (raw.parts) return raw; // POST shape
  const a_total = raw.a_total_cents ?? ((raw.a_used_cents || 0) + (raw.a_pending_return_cents || 0));
  const b_total = raw.b_total_cents ?? ((raw.b_received_cents || 0) + (raw.b_pending_return_cents || 0));
  return {
    close_run_id: raw.close_run_id,
    shop_id: raw.shop_id,
    period_start: raw.period_start,
    period_end_inclusive: raw.period_end_inclusive,
    status: raw.status,
    created_at: raw.created_at,
    finalized_at: raw.finalized_at,
    parts: {
      a_used_cents: raw.a_used_cents || 0,
      a_pending_return_cents: raw.a_pending_return_cents || 0,
      a_total_cents: a_total,
      b_received_cents: raw.b_received_cents || 0,
      b_pending_return_cents: raw.b_pending_return_cents || 0,
      b_total_cents: b_total,
      b0_cents: raw.b0_cents || 0,
      delta_wip_cents: raw.delta_wip_cents ?? (b_total - (raw.b0_cents || 0)),
      p_cents: raw.p_cents || 0,
      variance_cents: raw.variance_cents || 0,
      match_tally: {},
    },
    sublet: null,
  };
}

export default function MecDashboard() {
  const navigate = useNavigate();
  const { currentShopId, currentShop, shops: hubShops } = useShop();

  // ── Page state ──
  // Routing rows give us realm_id + parts_purchases wiring status per shop;
  // we still need them to detect "this shop isn't wired to QBO yet" so the
  // close run isn't run blindly.
  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingError, setRoutingError] = useState(null);

  // Period state: URL ?start=&end= → localStorage → defaultDashboardRange().
  // setPeriod(s, e) writes both URL (replaceState) and localStorage.
  const { periodStart, periodEnd, setPeriod } = usePeriodState('_global');

  const [close, setClose] = useState(null);        // normalized close result | {error} | null
  const [closeLoading, setCloseLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [bannerToast, setBannerToast] = useState(null); // {kind, message}

  // ── Load routing (for wiring detection + display name fallback) ──
  useEffect(() => {
    let alive = true;
    setRoutingLoading(true);
    setRoutingError(null);
    listRouting()
      .then((data) => {
        if (!alive) return;
        setRouting((data?.shops || []).filter((s) => s.shop_id !== 7515));
      })
      .catch((err) => {
        if (!alive) return;
        setRoutingError(err?.message || 'Failed to load shop list');
      })
      .finally(() => alive && setRoutingLoading(false));
    return () => { alive = false; };
  }, []);

  // Resolve the current shop's routing row (for realm_id/wiring) and a
  // best-effort display name. Prefer routing.shop_name; fall back to the
  // TopBar's hubShops nickname/name; fall back to "Shop {id}".
  const currentRouting = useMemo(
    () => routing.find((s) => s.shop_id === currentShopId) || null,
    [routing, currentShopId]
  );
  const currentShopName = useMemo(() => {
    if (currentRouting?.shop_name) return currentRouting.shop_name;
    const fromHub = hubShops?.find(
      (s) => s.shop_id === currentShopId || s.id === currentShopId
    );
    // /api/shops returns `shop_nickname` / `shop_name` (with the prefix);
    // useShop's currentShop may use `nickname`/`name` in some shapes.
    if (fromHub?.shop_nickname) return fromHub.shop_nickname;
    if (fromHub?.nickname) return fromHub.nickname;
    if (fromHub?.shop_name) return fromHub.shop_name;
    if (fromHub?.name) return fromHub.name;
    if (currentShop?.shop_nickname) return currentShop.shop_nickname;
    if (currentShop?.nickname) return currentShop.nickname;
    if (currentShop?.shop_name) return currentShop.shop_name;
    if (currentShop?.name) return currentShop.name;
    return currentShopId ? `Shop ${currentShopId}` : null;
  }, [currentRouting, currentShop, hubShops, currentShopId]);

  const shopIsWired = !!(currentRouting?.realm_id && currentRouting?.parts_purchases_account_id);

  // ── Fetch close for (currentShopId, periodStart, periodEnd) ──
  useEffect(() => {
    if (!currentShopId || !periodStart || !periodEnd) {
      setClose(null);
      return;
    }
    let alive = true;
    setCloseLoading(true);
    setClose(null);
    getClose(currentShopId, periodStart, periodEnd)
      .then((data) => {
        if (!alive) return;
        setClose(normalizeCloseResult(data));
      })
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          setClose(null); // no run yet — empty state
        } else {
          setClose({ error: err?.message || 'Failed to load close run' });
        }
      })
      .finally(() => alive && setCloseLoading(false));
    return () => { alive = false; };
  }, [currentShopId, periodStart, periodEnd]);

  // ── Run Close (single shop) ──
  const runOne = useCallback(async () => {
    if (!currentShopId) return;
    setBannerToast(null);
    setRunning(true);
    try {
      const result = await runClose(currentShopId, periodStart, periodEnd, false);
      setClose(normalizeCloseResult(result));
      setBannerToast({
        kind: 'success',
        message: `Close ran successfully for ${currentShopName}.`,
      });
    } catch (err) {
      setClose({ error: err?.message || 'Close run failed' });
      setBannerToast({
        kind: 'error',
        message: err?.message || 'Close run failed.',
      });
    } finally {
      setRunning(false);
      setTimeout(() => setBannerToast(null), 8000);
    }
  }, [currentShopId, currentShopName, periodStart, periodEnd]);

  // ── Single-shop summary numbers (no aggregation across shops) ──
  const summary = useMemo(() => {
    const acc = {
      p: 0, a: 0, b: 0, b0: 0, delta_wip: 0,
      variance_positive: 0,
      variance_negative: 0,
      variance_total: 0,
      variance_pct: 0,
      pending_review_cents: 0,
      ambiguous_cents: 0,
      tier_3_no_po_count: 0,
      has_data: false,
    };
    const r = close;
    if (r?.parts) {
      const p = r.parts;
      acc.has_data = true;
      acc.p = p.p_cents || 0;
      acc.a = p.a_total_cents || ((p.a_used_cents || 0) + (p.a_pending_return_cents || 0));
      acc.b = p.b_total_cents || ((p.b_received_cents || 0) + (p.b_pending_return_cents || 0));
      acc.b0 = p.b0_cents || 0;
      acc.delta_wip = p.delta_wip_cents || 0;
      const v = p.variance_cents || 0;
      if (v >= 0) acc.variance_positive = v;
      else acc.variance_negative = -v;
      acc.variance_total = acc.variance_positive - acc.variance_negative;
      acc.variance_pct = acc.p > 0 ? (acc.variance_total / acc.p) * 100 : 0;
      const tally = p.match_tally || {};
      acc.pending_review_cents = tally.total_pending_review_cents || 0;
      acc.ambiguous_cents = tally.total_ambiguous_cents || 0;
      acc.tier_3_no_po_count = tally.tier_3_no_po || 0;
    }
    return acc;
  }, [close]);

  const cents = (v) => formatMoney((v || 0) / 100);

  const noShopSelected = !currentShopId;
  const noRunYet = !!currentShopId && !closeLoading && !close;
  const closeError = close?.error || null;

  // Single-shop array for the InventoryExclusionCard (component already
  // supports an array of shops; we just pass one row).
  const inventoryShops = useMemo(() => {
    if (!currentShopId) return [];
    return [{
      shop_id: currentShopId,
      shop_name: currentShopName || `Shop ${currentShopId}`,
    }];
  }, [currentShopId, currentShopName]);

  return (
    <div>
      <PageHeader
        title="Month-End Close"
        description="Reconcile QuickBooks parts spend against Tekmetric usage and WIP for the selected shop. Drift becomes leakage, not surprise."
        actions={
          <>
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Settings size={15} />}
              onClick={() => navigate('/mec/settings/routing')}
            >
              Account Routing
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={running ? null : <Play size={15} />}
              loading={running}
              onClick={runOne}
              disabled={!currentShopId || running}
            >
              {running
                ? `Running…`
                : currentShopName
                  ? `Run Close for ${currentShopName}`
                  : 'Run Close'}
            </Button>
          </>
        }
      />

      {/* Breadcrumb-style link to multi-shop comparison */}
      <div className="-mt-2 mb-5">
        <button
          type="button"
          onClick={() => navigate('/mec/locations')}
          className="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)] transition-colors group"
        >
          <MapPin size={12} />
          Compare all locations
          <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* Routing error — major blocker, show clearly */}
      {routingError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[14px] font-semibold">Couldn't load shop list.</div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-600)]">{routingError}</div>
              <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
                The MEC backend may not be deployed or your session expired.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* No shop selected — nudge to TopBar dropdown */}
      {noShopSelected && !routingLoading && (
        <Card className="mb-5 bg-[var(--color-action-50)] border-[var(--color-action-100)]">
          <div className="flex items-start gap-3 py-1">
            <Building2 size={18} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-[var(--color-action-800)]">
                Select a shop to begin.
              </div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-700)]">
                Use the shop selector in the top-right to pick which location's
                close you want to inspect. The Dashboard is a single-shop deep-dive;
                cross-shop comparison lives on the Locations page.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Selected shop isn't wired — soft nudge */}
      {currentShopId && !routingLoading && currentRouting && !shopIsWired && (
        <Card className="mb-5 bg-[var(--color-action-50)] border-[var(--color-action-100)]">
          <div className="flex items-start gap-3 py-1">
            <HelpCircle size={18} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-[var(--color-action-800)]">
                {currentShopName} isn't wired to QuickBooks yet.
              </div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-700)]">
                Before the close math can run, this shop needs its QBO Parts
                Purchases account selected. Takes about a minute.
              </div>
              <div className="mt-2">
                <Button
                  variant="action"
                  size="sm"
                  leftIcon={<Settings size={13} />}
                  onClick={() => navigate('/mec/settings/routing')}
                >
                  Open Account Routing
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filter bar — period picker only; shop comes from TopBar */}
      <Card className="mb-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PeriodPicker start={periodStart} end={periodEnd} onChange={(s, e) => setPeriod(s, e)} />
          {currentShopId && currentShopName && (
            <div className="inline-flex items-center gap-2 text-[12px] text-[var(--color-warm-600)]">
              <Building2 size={13} className="text-[var(--color-warm-500)]" />
              <span>
                Viewing <span className="font-semibold text-[var(--color-warm-900)]">{currentShopName}</span>
              </span>
              <Badge tone={shopIsWired ? 'recovery' : 'cream'} size="xs">
                {shopIsWired ? 'Wired' : 'Not wired'}
              </Badge>
            </div>
          )}
        </div>
      </Card>

      {/* Toast for run completion */}
      {bannerToast && (
        <div className={`
          mb-5 p-3 rounded-[var(--radius-md)] text-[13px] flex items-start gap-2
          ${bannerToast.kind === 'success'
            ? 'bg-[var(--color-success-50)] border border-[var(--color-success-100)] text-[var(--color-success-700)]'
            : bannerToast.kind === 'mixed'
              ? 'bg-[var(--color-action-50)] border border-[var(--color-action-100)] text-[var(--color-action-700)]'
              : 'bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[var(--color-danger-700)]'}
        `}>
          {bannerToast.kind === 'success' ? (
            <CheckCircle2 size={15} className="shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          )}
          <div>{bannerToast.message}</div>
        </div>
      )}

      {/* Empty-state when no close run yet for the selected shop/period */}
      {currentShopId && noRunYet && !closeError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1">
            <RefreshCcw size={18} className="shrink-0 mt-0.5 text-[var(--color-warm-500)]" />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
                No close run yet for {currentShopName} ({periodStart} → {periodEnd}).
              </div>
              <div className="text-[12px] mt-1 text-[var(--color-warm-600)]">
                Click <span className="font-semibold">Run Close for {currentShopName}</span> above to fetch QBO purchases,
                match against Tekmetric, and compute variance for this period.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Error fetching this shop's close */}
      {currentShopId && closeError && (
        <Card className="mb-5 bg-[var(--color-danger-50)] border-[var(--color-danger-100)]">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[14px] font-semibold">Couldn't load close run.</div>
              <div className="text-[12px] mt-1">{closeError}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Top-line summary cards — single-shop numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 stagger-children">
        <MetricTile
          label="Purchases (P)"
          value={cents(summary.p)}
          sub="QuickBooks Parts Purchases this period"
          accent="neutral"
          icon={<ShoppingCart />}
          loading={closeLoading}
        />
        <MetricTile
          label="Matched COGS (A)"
          value={cents(summary.a)}
          sub="Parts used on closed ROs + pending returns"
          accent="recovery"
          icon={<Wrench />}
          loading={closeLoading}
        />
        <MetricTile
          label="WIP (B)"
          value={cents(summary.b)}
          sub="Received & attached to open ROs"
          accent="insight"
          icon={<DollarSign />}
          loading={closeLoading}
        />
        <MetricTile
          label="ΔWIP"
          value={(summary.delta_wip >= 0 ? '+' : '') + cents(summary.delta_wip)}
          sub={`From B₀ ${cents(summary.b0)} → B ${cents(summary.b)}`}
          accent={summary.delta_wip >= 0 ? 'action' : 'recovery'}
          icon={summary.delta_wip >= 0 ? <TrendingUp /> : <TrendingDown />}
          loading={closeLoading}
        />
      </div>

      {/* Variance split — D-022 sign distinction */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <MetricTile
          label="Positive Variance (Leakage)"
          value={cents(summary.variance_positive)}
          sub="Spent in QBO but not in Matched or WIP — investigate"
          accent={
            summary.p > 0 && (summary.variance_positive / summary.p) * 100 > 10 ? 'danger'
              : summary.p > 0 && (summary.variance_positive / summary.p) * 100 > 5 ? 'action'
                : 'neutral'
          }
          icon={<ArrowUpRight />}
          loading={closeLoading}
        />
        <MetricTile
          label="Negative Variance (Recovery)"
          value={cents(summary.variance_negative)}
          sub="Refunds, credits, prior-period offsets — money back in"
          accent="recovery"
          icon={<ArrowDownRight />}
          loading={closeLoading}
        />
        <MetricTile
          label="Net Variance %"
          value={`${summary.variance_pct >= 0 ? '+' : ''}${summary.variance_pct.toFixed(1)}%`}
          sub={`Net ${cents(summary.variance_total)} ÷ P`}
          accent={Math.abs(summary.variance_pct) > 10 ? 'danger' : Math.abs(summary.variance_pct) > 5 ? 'action' : 'neutral'}
          icon={<DollarSign />}
          loading={closeLoading}
        />
      </div>

      {/* Auditable cards row — all scoped to the single current shop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <InventoryExclusionCard
          selectedShops={inventoryShops}
          periodStart={periodStart}
          periodEndInclusive={periodEnd}
        />

        <div className="space-y-4">
          <MetricTile
            label={`Pending Heuristic Matches (${bucketLabel('pending_review')})`}
            value={cents(summary.pending_review_cents)}
            sub="Tier 2 matches awaiting human review (Mode A queue)"
            accent="action"
            icon={<ListChecks />}
            onClick={currentShopId
              ? () => navigate(`/mec/tier-2-review?shop_id=${currentShopId}`)
              : undefined}
            loading={closeLoading}
          />
          <MetricTile
            label={bucketLabel('ambiguous', 'Ambiguous — Needs Review')}
            value={cents(summary.ambiguous_cents)}
            sub="QBO sum exceeds Tekmetric PO total (suspected double-entry)"
            accent="insight"
            icon={<HelpCircle />}
            onClick={currentShopId
              ? () => navigate(`/mec/tier-2-review?shop_id=${currentShopId}&filter=ambiguous`)
              : undefined}
            loading={closeLoading}
          />
          <MetricTile
            label="No PO# in QBO memo"
            value={`${summary.tier_3_no_po_count} item${summary.tier_3_no_po_count === 1 ? '' : 's'}`}
            sub="Biggest single variance driver — drill down for dollars + vendor breakdown"
            accent="danger"
            icon={<Search />}
            onClick={currentShopId
              ? () => navigate(`/mec/location/${currentShopId}?filter=tier_3_no_po`)
              : undefined}
            loading={closeLoading}
          />
        </div>
      </div>

      {/* Recent closes — quick re-entry into prior close runs */}
      <div className="mb-6">
        <RecentClosesPanel
          onRowClick={({ shop_id, period_start, period_end }) =>
            navigate(`/mec/location/${shop_id}?start=${period_start}&end=${period_end}`)
          }
        />
      </div>

      {/* Drill-down CTA — replaces the per-shop comparison table */}
      {currentShopId && (
        <Card className="mb-4 bg-[var(--color-cream-50)] border-[var(--color-warm-200)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-[var(--color-warm-900)]">
                Drill into {currentShopName}
              </div>
              <div className="text-[12px] text-[var(--color-warm-500)] mt-0.5">
                See matched ROs, WIP attachments, and Tier 1/2/3 detail for this shop.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="md"
                leftIcon={<MapPin size={14} />}
                onClick={() => navigate('/mec/locations')}
              >
                Compare all locations
              </Button>
              <Button
                variant="primary"
                size="md"
                rightIcon={<ArrowRight size={15} />}
                onClick={() => navigate(`/mec/location/${currentShopId}`)}
                disabled={!summary.has_data}
              >
                View Drill-Down for {currentShopName}
              </Button>
            </div>
          </div>
          {!summary.has_data && !closeLoading && (
            <div className="mt-3 text-[11px] text-[var(--color-warm-500)]">
              Drill-down activates once a close has been run for this shop and period.
            </div>
          )}
        </Card>
      )}

      {/* Footer hint */}
      <div className="mt-6 text-[11px] text-[var(--color-warm-500)] flex items-center gap-2 justify-end">
        <RefreshCcw size={11} />
        Draft mode — close runs save numbers but don't lock the period. Use Finalize in Journal Entry to snapshot.
      </div>
    </div>
  );
}
